import { Box, styled } from '@mui/material'
import React, { RefObject, forwardRef, useImperativeHandle, useRef } from 'react'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { computeTooltipOffset } from './drawingGeometry.action'

export interface DrawingTooltipHandle {
  show: (event: React.PointerEvent, title: string, detail: string) => void
  move: (event: React.PointerEvent) => void
  hide: () => void
}

interface DrawingTooltipProps {
  containerRef: RefObject<HTMLElement>
}

const DrawingTooltip = forwardRef<DrawingTooltipHandle, DrawingTooltipProps>(({ containerRef }, ref) => {
  const tooltipRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLElement>(null)
  const detailRef = useRef<HTMLElement>(null)

  useImperativeHandle(
    ref,
    () => {
      const move = (event: React.PointerEvent) => {
        const tooltip = tooltipRef.current
        const container = containerRef.current
        if (!tooltip || tooltip.style.display === 'none' || !container) return
        const offset = computeTooltipOffset(
          event.clientX,
          event.clientY,
          container.getBoundingClientRect(),
          tooltip.offsetWidth,
          tooltip.offsetHeight,
        )
        tooltip.style.transform = `translate(${offset.x}px, ${offset.y}px)`
      }
      return {
        move,
        show: (event, title, detail) => {
          if (!tooltipRef.current || !titleRef.current || !detailRef.current) return
          titleRef.current.textContent = title
          detailRef.current.textContent = detail
          tooltipRef.current.style.display = 'flex'
          move(event)
        },
        hide: () => {
          if (tooltipRef.current) tooltipRef.current.style.display = 'none'
        },
      }
    },
    [containerRef],
  )

  return (
    <TooltipCard ref={tooltipRef}>
      <TooltipTitle ref={titleRef} />
      <TooltipDetail ref={detailRef} />
    </TooltipCard>
  )
})

export default DrawingTooltip

const TooltipCard = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  zIndex: 5,
  pointerEvents: 'none',
  maxWidth: 280,
  backgroundColor: getThemeColor(theme, MUIThemeColors.primaryDark),
  color: getThemeColor(theme, MUIThemeColors.white),
  borderRadius: 4,
  padding: theme.spacing(1, 1.25),
  fontSize: 12,
  lineHeight: 1.4,
  boxShadow: '0 4px 12px rgba(0,0,0,.2)',
  display: 'none',
  flexDirection: 'column',
}))

const TooltipTitle = styled('b')({ fontWeight: 600, fontSize: 13 })

const TooltipDetail = styled('span')({ opacity: 0.88, fontWeight: 500, whiteSpace: 'pre-line' })
